import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../components/ui/button/button";
import { Item, ItemStatus } from "../../components/ui/item-status/item-status";
import { Skeleton } from "../../components/ui/skeleton/skeleton";
import { StatusOption } from "../../components/ui/status-popup/status-popup";
import { ResultType, useMTUStatuses } from "../../queries/mtu/use-mtu-statuses";
import { useCompleteOperation } from "../../queries/operation/use-complete-operation";
import { useTasks } from "../../queries/tasks/use-tasks";
import { useMTUsWithStatus } from "./use-mtus-with-status";
import { StatusSplitButton } from "@/components/ui/status-split-button/status-split-button";
import { ErrorPopover } from "@/components/ui/error-popover/error-popover";
import { QueryError } from "@/components/ui/query-error/query-error";
import { useMTUs } from "@/queries/mtu/use-mtus";

const statusToStatusOption = (x: ResultType) => ({
  id: x.ResultValue ?? "",
  label: x.ResultValue ?? "",
  type: x.IsKO ? ("failure" as const) : ("success" as const),
});

export function CompleteOperation({ onSuccess }: { onSuccess?: VoidFunction }) {
  const { t } = useTranslation();
  const {
    isPending: isPendingTasks,
    error: tasksError,
    refetch: refetchTasks,
  } = useTasks();
  const { error: mtuError, refetch: refetchMtus } = useMTUs();
  const {
    statuses,
    isPending: isPendingStatuses,
    selectedMTUStatus,
    setSelectedStatus,
    error: statusesError,
    refetch: refetchStatuses,
  } = useMTUStatuses();
  const {
    completeOperation,
    isPending: isPendingCompleteOperation,
    error: completeOperationError,
  } = useCompleteOperation({
    onSuccess,
  });
  const { mtusWithStatus, handleStatusChange } = useMTUsWithStatus();

  const options: StatusOption[] = statuses.map(statusToStatusOption);
  const readyItems = mtusWithStatus.filter(isReadyItem);
  const selectedOption = options.find(
    (x) => x.id === selectedMTUStatus?.ResultValue
  );

  const handleSetSelectedStatus = useCallback(
    (val: string) => {
      setSelectedStatus(val);
    },
    [setSelectedStatus]
  );

  const handleChangeAllStatus = useCallback(() => {
    if (selectedOption)
      mtusWithStatus.forEach((x) =>
        handleStatusChange(x.id, selectedOption.id)
      );
  }, [mtusWithStatus, handleStatusChange, selectedOption]);

  const handleComplete = useCallback(() => {
    completeOperation(
      readyItems.map((x) => ({ id: x.id, nId: x.label, value: x.statusId }))
    );
  }, [completeOperation, readyItems]);

  if (statusesError || tasksError || mtuError) {
    const retries = [
      statusesError && refetchStatuses,
      tasksError && refetchTasks,
      mtuError && refetchMtus,
    ];
    return (
      <QueryError
        errors={[statusesError, tasksError, mtuError]}
        retries={retries}
      />
    );
  }

  return (
    <>
      <div className="grid grid-cols-2  gap-2">
        {(isPendingTasks || isPendingStatuses) &&
          [...Array(8)].map((_, i) => (
            <Skeleton className="h-[51px] w-full" key={i} />
          ))}
        {!isPendingTasks &&
          !isPendingStatuses &&
          mtusWithStatus.map((item) => (
            <ItemStatus
              key={item.id}
              item={item}
              statusOptions={options}
              onStatusChange={handleStatusChange}
            />
          ))}
      </div>

      <div className="flex gap-3 justify-end mt-auto">
        {!isPendingStatuses && !isPendingStatuses && options.length > 0 && (
          <>
            <StatusSplitButton
              options={options}
              onValueChange={handleSetSelectedStatus}
              value={selectedOption?.id ?? ""}
              onSelect={handleChangeAllStatus}
              disabled={isPendingCompleteOperation || isPendingTasks}
            />
            <ErrorPopover
              error={completeOperationError}
              options={{ align: "end" }}
            >
              <Button
                className="rounded-sm"
                disabled={
                  readyItems.length === 0 ||
                  isPendingCompleteOperation ||
                  isPendingTasks
                }
                onClick={handleComplete}
                loading={isPendingCompleteOperation}
              >
                {t("completeOperation.complete", {
                  x: readyItems.length !== 0 ? readyItems.length : "",
                })}
              </Button>
            </ErrorPopover>
          </>
        )}
      </div>
    </>
  );
}

const isReadyItem = (item: Item): item is Required<Item> => !!item.statusId;
